import { useMemo } from "react";

import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { useSettings } from "@/providers/general/settings-provider";

import { PRIORITY_DEPARTMENTS, type PriorityDepartment } from "../utils/priority-report.utils";

type UsePriorityReportDepartmentsReturn = {
    selectedDepts: string[];
    activeDepartments: PriorityDepartment[];
    toggleDepartment: (key: string) => void;
};

/**
 * Manages which departments are shown in the Priority Report (full page and dashboard widget).
 * Uses the remote config / settings system so changes are persisted to the
 * backend and synced in real time via the config socket.
 */
export const usePriorityReportDepartments = (): UsePriorityReportDepartmentsReturn => {
    const { currentSettings, setValue } = useSettings();

    const selectedDepts = currentSettings[SETTINGS_KEYS.PRIORITY_REPORT_DEPARTMENTS] ?? [];

    const toggleDepartment = (key: string) => {
        const next = selectedDepts.includes(key) ? selectedDepts.filter((k) => k !== key) : [...selectedDepts, key];
        setValue(SETTINGS_KEYS.PRIORITY_REPORT_DEPARTMENTS, next);
    };

    const activeDepartments = useMemo(
        () => PRIORITY_DEPARTMENTS.filter((d) => selectedDepts.includes(d.key)),
        [selectedDepts]
    );

    return { selectedDepts, activeDepartments, toggleDepartment };
};
